import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Avatar,
  IconButton,
  Tooltip,
  Stack,
  Fab,
  alpha,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  ContentCopy,
  Share,
  QrCode2,
  Verified,
} from '@mui/icons-material';
import { GradientCard, GlassCard, CoinCard } from '../../components/v2';
import { gradients } from '../../theme/v2Theme';
import QRCode from 'react-qr-code';
import { useParams } from 'react-router-dom';
import {
  isHandleAvailable,
  isSubHandleAvailable,
  getUserAddresses,
} from '../../utils/handleManager';
import Coins from '../../assets/coins/coins.json';
import * as firebase from 'firebase/app';
import 'firebase/database';
import { hasEthAddress, handleToEnsStyle } from '../../utils/ensFormatter';

const coinGradients: { [key: string]: string } = {
  BTC: gradients.sunset,
  ETH: gradients.ocean,
  USDC: gradients.success,
  USDT: gradients.success,
  MATIC: gradients.lavender,
  SOL: gradients.primary,
  BNB: gradients.sunset,
  AVAX: gradients.primary,
  DOT: gradients.lavender,
  LINK: gradients.ocean,
};

export const ProfilePage: React.FC = () => {
  const { handle } = useParams<{ handle: string }>();
  const [showQR, setShowQR] = useState(false);
  const [loading, setLoading] = useState(true);
  const [handleExists, setHandleExists] = useState(false);
  const [addresses, setAddresses] = useState<{ [key: string]: string }>({});
  const [bio, setBio] = useState<string>('Accepting crypto payments');
  const [avatar, setAvatar] = useState<string | null>(null);

  const coins = Coins as { [key: string]: any };

  useEffect(() => {
    async function checkHandleAndLoadData() {
      if (!handle) {
        setLoading(false);
        return;
      }

      try {
        // Check if handle exists
        const isAvailable = await isHandleAvailable(handle);
        const isSubAvailable = await isSubHandleAvailable(handle);

        let exists = false;
        if (isAvailable) {
          if (!isSubAvailable) {
            exists = true;
          }
        } else {
          exists = true;
        }

        setHandleExists(exists);

        // Load addresses if handle exists
        if (exists) {
          const userAddresses = await getUserAddresses(handle);
          setAddresses(userAddresses);

          // Resolve handle -> uid from /handles/{handle}, then fetch bio/avatar by uid
          try {
            const handleRef = firebase.database().ref(`/handles/${handle}`);
            const handleSnap = await handleRef.once('value');
            if (handleSnap.exists()) {
              const uid = handleSnap.val();
              if (uid) {
                // bio by uid
                const bioRef = firebase.database().ref(`/profiles/bio/${uid}`);
                const bioSnapshot = await bioRef.once('value');
                if (bioSnapshot.exists()) {
                  const bioValue = bioSnapshot.val();
                  if (bioValue) setBio(bioValue);
                }

                // avatar by uid
                const avatarRef = firebase.database().ref(`/profiles/avatar/${uid}`);
                const avatarSnapshot = await avatarRef.once('value');
                if (avatarSnapshot.exists()) {
                  const avatarValue = avatarSnapshot.val();
                  if (avatarValue && avatarValue !== 'none' && avatarValue !== '') {
                    setAvatar(avatarValue);
                  }
                }
              }
            } else {
              // Fallback: try to read profile by handle (legacy data layout)
              const bioRef = firebase.database().ref(`/profiles/bio/${handle}`);
              const bioSnapshot = await bioRef.once('value');
              if (bioSnapshot.exists()) {
                const bioValue = bioSnapshot.val();
                if (bioValue) setBio(bioValue);
              }

              const avatarRef = firebase.database().ref(`/profiles/avatar/${handle}`);
              const avatarSnapshot = await avatarRef.once('value');
              if (avatarSnapshot.exists()) {
                const avatarValue = avatarSnapshot.val();
                if (avatarValue && avatarValue !== 'none' && avatarValue !== '') {
                  setAvatar(avatarValue);
                }
              }
            }
          } catch (fbError) {
            console.error('Error resolving handle -> uid or fetching bio/avatar:', fbError);
            // Keep defaults if Firebase fetch fails
          }
          
          // Track profile view - increment view count for this user
          try {
            const handleRef = firebase.database().ref(`/handles/${handle}`);
            const handleSnap = await handleRef.once('value');
            if (handleSnap.exists()) {
              const uid = handleSnap.val();
              if (uid) {
                // Increment profile views
                firebase.database().ref(`/stats/${uid}/profileViews`).transaction((current) => {
                  return (current || 0) + 1;
                });
              }
            }
          } catch (viewError) {
            console.error('Error tracking profile view:', viewError);
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    }

    checkHandleAndLoadData();
  }, [handle]);

  const profileUrl = `https://addrs.to/${handle}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(profileUrl);
    // Show toast
  };

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    // Show toast
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${handle} on addrs`,
          text: `Check out my crypto payment profile!`,
          url: profileUrl,
        });
      } catch (err) {
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#f8fafc',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading profile...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Handle not found
  if (!handleExists) {
    return (
      <Box sx={{ minHeight: '100vh', background: '#f8fafc' }}>
        <Box
          sx={{
            background: gradients.mesh,
            py: 8,
            pb: 4,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '0 0 60px 60px',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 100%)',
            },
          }}
        >
          <Container maxWidth="md">
            <Box position="relative" zIndex={1} textAlign="center">
              <Typography variant="h3" fontWeight={800} sx={{ color: '#fff', mb: 2 }}>
                Handle Not Found
              </Typography>
              <Typography variant="h6" sx={{ color: alpha('#fff', 0.9) }}>
                @{handle} doesn't exist yet
              </Typography>
            </Box>
          </Container>
        </Box>
        <Container maxWidth="lg" sx={{ mt: 6, pb: 8 }}>
          <GlassCard sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Want to claim this handle?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your free addrs profile and start accepting crypto in minutes
            </Typography>
            <Box
              component="a"
              href="/"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 3,
                background: gradients.primary,
                color: '#fff',
                fontWeight: 700,
                textDecoration: 'none',
                display: 'inline-block',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0px 8px 24px rgba(99, 102, 241, 0.3)',
                },
              }}
            >
              Sign Up Free
            </Box>
          </GlassCard>
        </Container>
      </Box>
    );
  }

  // Convert addresses object to array for display
  const addressArray = Object.keys(addresses).map((coinKey) => {
    const coinData = coins[coinKey] || {};
    return {
      coin: coinData.name || coinKey,
      symbol: coinKey,
      address: addresses[coinKey],
      gradient: coinGradients[coinKey] || gradients.primary,
      icon: coinData.symbol || coinKey.charAt(0),
    };
  });

  return (
    <Box sx={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Header */}
      <Box
        sx={{
          background: gradients.mesh,
          py: 8,
          pb: 4,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '0 0 60px 60px',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 100%)',
          },
        }}
      >
        <Container maxWidth="md">
          <Box position="relative" zIndex={1} textAlign="center">
            <Box position="relative" sx={{ display: 'inline-block' }}>
              <Avatar
                src={avatar || undefined}
                sx={{
                  width: 140,
                  height: 140,
                  border: '6px solid white',
                  boxShadow: '0px 12px 48px rgba(0,0,0,0.2)',
                  fontSize: '4rem',
                  fontWeight: 800,
                  background: gradients.secondary,
                  margin: '0 auto',
                }}
              >
                {!avatar && handle?.charAt(0).toUpperCase()}
              </Avatar>
            </Box>

            <Stack spacing={1} sx={{ mt: 3 }}>
              <Typography
                variant="h4"
                sx={{ color: alpha('#fff', 0.9), fontWeight: 700 }}
              >
                @{handle}
              </Typography>

              {/* Show ENS if user has ETH address */}
              {hasEthAddress(addresses) && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      background: alpha('#fff', 0.15),
                      backdropFilter: 'blur(10px)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: alpha('#fff', 0.25),
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText(handleToEnsStyle(handle));
                    }}
                  >
                    <Typography variant="body1" fontWeight={700} sx={{ color: '#fff' }}>
                      {handleToEnsStyle(handle)}
                    </Typography>
                    <Chip
                      label="ENS"
                      size="small"
                      sx={{
                        background: alpha('#fff', 0.25),
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        height: '20px',
                      }}
                    />
                  </Box>
                </Box>
              )}

              <Typography
                variant="body1"
                sx={{ color: alpha('#fff', 0.85), mx: 'auto', mt: 2, textAlign: 'center' }}
              >
                {bio}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              sx={{ mt: 4 }}
            >
              <Tooltip title="Copy Profile Link">
                <IconButton
                  sx={{
                    background: alpha('#fff', 0.2),
                    backdropFilter: 'blur(10px)',
                    color: '#fff',
                    '&:hover': {
                      background: alpha('#fff', 0.3),
                      transform: 'scale(1.1)',
                    },
                  }}
                  onClick={handleCopyLink}
                >
                  <ContentCopy />
                </IconButton>
              </Tooltip>
              <Tooltip title="Share Profile">
                <IconButton
                  sx={{
                    background: alpha('#fff', 0.2),
                    backdropFilter: 'blur(10px)',
                    color: '#fff',
                    '&:hover': {
                      background: alpha('#fff', 0.3),
                      transform: 'scale(1.1)',
                    },
                  }}
                  onClick={handleShare}
                >
                  <Share />
                </IconButton>
              </Tooltip>
              <Tooltip title="Show QR Code">
                <IconButton
                  sx={{
                    background: alpha('#fff', 0.2),
                    backdropFilter: 'blur(10px)',
                    color: '#fff',
                    '&:hover': {
                      background: alpha('#fff', 0.3),
                      transform: 'scale(1.1)',
                    },
                  }}
                  onClick={() => setShowQR(!showQR)}
                >
                  <QrCode2 />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 2, pb: 8 }}>
        {/* Addresses Grid */}
        <Box>
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: '#8b5cf6', maxWidth: 500, mx: 'auto' }}>
              {handle} Accepts Payment In
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Scan QR code or click to view payment options
            </Typography>
          </Box>

          {addressArray.length > 0 ? (
            <Grid container spacing={3}>
              {addressArray.map((address, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <CoinCard
                    coinName={address.coin}
                    coinSymbol={address.symbol}
                    address={address.address}
                    icon={address.icon}
                    gradient={address.gradient}
                    handle={handle}
                    userAddresses={addresses}
                    qrValue={`https://addrs.to/${handle}/${address.symbol.toLowerCase()}`}
                    onCopy={() => {
                      const link = `https://addrs.to/${handle}/${address.symbol.toLowerCase()}`;
                      navigator.clipboard.writeText(link);
                    }}
                    onSend={() => {}}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <GlassCard sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                This profile doesn't have any addresses set up yet.
              </Typography>
            </GlassCard>
          )}
        </Box>

        {/* Info Card */}
        <Box sx={{ mt: 6 }}>
          <GlassCard sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Want your own crypto payment profile?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your free addrs profile and start accepting crypto in minutes
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
            >
              <Box
                component="a"
                href="/"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  background: gradients.primary,
                  color: '#fff',
                  fontWeight: 700,
                  textDecoration: 'none',
                  display: 'inline-block',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0px 8px 24px rgba(99, 102, 241, 0.3)',
                  },
                }}
              >
                Sign Up Free
              </Box>
              <Box
                component="a"
                href="/about"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  border: '2px solid',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  fontWeight: 700,
                  textDecoration: 'none',
                  display: 'inline-block',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: alpha('#6366f1', 0.05),
                    transform: 'scale(1.05)',
                  },
                }}
              >
                Learn More
              </Box>
            </Stack>
          </GlassCard>
        </Box>
      </Container>

      {/* Floating QR Code for Profile */}
      {showQR && (
        <GlassCard
          sx={{
            position: 'fixed',
            bottom: 3,
            right: 3,
            p: 2,
            zIndex: 1000,
            maxWidth: 200,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Scan to Visit
            </Typography>
            <Box sx={{ p: 2, background: '#fff', borderRadius: 2 }}>
              <QRCode value={profileUrl} size={150} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              @{handle}
            </Typography>
          </Box>
        </GlassCard>
      )}

      {/* Floating Action Button */}
      <Tooltip title={showQR ? 'Hide QR' : 'Show QR'}>
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 24,
            left: 24,
            background: gradients.primary,
            '&:hover': {
              background: gradients.primary,
              opacity: 0.9,
            },
          }}
          onClick={() => setShowQR(!showQR)}
        >
          <QrCode2 />
        </Fab>
      </Tooltip>
    </Box>
  );
};
